const ATTIVITA_LABELS: Record<string, string> = {
  CentroRaccolta: "Centro di raccolta",
  Produzione: "Produzione",
  Recupero: "Recupero",
  Smaltimento: "Smaltimento",
  Trasporto: "Trasporto",
  IntermediazioneSenzaDetenzione: "Intermediazione senza detenzione",
};

const OPERAZIONI_LABELS: Record<string, string> = {
  R1: "R1", R2: "R2", R3: "R3", R4: "R4", R5: "R5", R6: "R6", R7: "R7",
  R8: "R8", R9: "R9", R10: "R10", R11: "R11", R12: "R12", R13: "R13",
  D1: "D1", D2: "D2", D3: "D3", D4: "D4", D5: "D5", D6: "D6", D7: "D7",
  D8: "D8", D9: "D9", D10: "D10", D11: "D11", D12: "D12", D13: "D13",
  D14: "D14", D15: "D15",
};

export type RegistroRow = Record<string, unknown>;

export function registroIdentificativo(r: RegistroRow): string {
  return String(r.identificativo ?? r.id ?? "");
}

export function registroDescrizione(r: RegistroRow): string {
  const d = r.descrizione ?? r.description;
  if (typeof d === "string" && d.trim()) return d.trim();
  return "—";
}

export function registroAttivita(r: RegistroRow): string {
  const raw = r.attivita ?? r.attivitaRegistro;
  const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const labels = items
    .map((a) => ATTIVITA_LABELS[String(a)] ?? String(a))
    .filter(Boolean);
  const rec = r.attivita_rec_smalt ?? r.attivitaRecSmalt;
  const recItems = Array.isArray(rec) ? rec : rec ? [rec] : [];
  const recLabels = recItems
    .map((o) => OPERAZIONI_LABELS[String(o)] ?? String(o))
    .filter(Boolean);
  if (labels.length === 0 && recLabels.length === 0) return "—";
  if (recLabels.length === 0) return labels.join(", ");
  if (labels.length === 0) return recLabels.join(", ");
  return `${labels.join(", ")} · ${recLabels.join(", ")}`;
}

export function registroAnnualita(r: RegistroRow): number | null {
  const raw = r.data_creazione ?? r.dataCreazione ?? r.data_apertura ?? r.dataApertura;
  if (!raw) return null;
  const y = new Date(String(raw)).getFullYear();
  return Number.isFinite(y) ? y : null;
}

/** Come portale RENTRI: "1 (per l'annualità 2026)" oppure "— (per l'annualità 2026)". */
export function registroProgressivoIniziale(r: RegistroRow): string {
  const anno = registroAnnualita(r);
  const suffix = anno ? ` (per l'annualità ${anno})` : "";
  const prog =
    r.progressivo_inziale_locale ??
    r.progressivoInzialeLocale ??
    r.progressivo_iniziale;
  if (prog === 0 || prog === "0") return `0${suffix}`;
  if (prog !== null && prog !== undefined && String(prog).trim() !== "") {
    return `${prog}${suffix}`;
  }
  return `—${suffix}`;
}

export function registroDataApertura(r: RegistroRow): string {
  const raw =
    r.data_creazione ??
    r.dataCreazione ??
    r.data_apertura ??
    r.dataApertura;
  if (!raw) return "—";
  const date = new Date(String(raw));
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function registroStato(r: RegistroRow): string {
  return String(r.stato ?? "—");
}

export function registroStatoVariant(
  stato: string,
): "success" | "secondary" | "outline" {
  const s = stato.toLowerCase();
  if (s === "attivo" || s === "aperto") return "success";
  if (s === "chiuso") return "secondary";
  return "outline";
}

// ---------------------------------------------------------------------------
// Stato UI unificato : bozza | errore | in_elaborazione | attivo | chiuso
// Calcolato lato backend da `registri_enriched.py` ma mantiene un fallback locale.
// ---------------------------------------------------------------------------

export type RegistroStatoUi =
  | "bozza"
  | "errore"
  | "in_elaborazione"
  | "attivo"
  | "chiuso";

export function resolveRegistroStatoUi(r: RegistroRow): RegistroStatoUi {
  const explicit = r.stato_ui;
  if (typeof explicit === "string") {
    const s = explicit.toLowerCase();
    if (s === "bozza" || s === "errore" || s === "in_elaborazione" || s === "attivo" || s === "chiuso") {
      return s as RegistroStatoUi;
    }
  }
  const stato = String(r.stato ?? "").toLowerCase();
  if (stato === "chiuso") return "chiuso";
  if (stato === "attivo" || stato === "aperto") return "attivo";
  if (r._virtual) return "bozza";
  return "attivo";
}

export function registroStatoUiLabel(s: RegistroStatoUi): string {
  switch (s) {
    case "bozza":
      return "Bozza";
    case "errore":
      return "Errore";
    case "in_elaborazione":
      return "In elaborazione";
    case "attivo":
      return "Attivo";
    case "chiuso":
      return "Chiuso";
  }
}

export function registroDraftId(r: RegistroRow): string | null {
  const v = r.draft_id;
  return typeof v === "string" && v ? v : null;
}

export function registroErrorDetail(r: RegistroRow): string | null {
  const v = r.error_detail;
  return typeof v === "string" && v ? v : null;
}
